import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Space,
  message,
  Popconfirm,
  Tag,
  Card,
  Empty,
} from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import {
  getTransactions,
  getCategories,
  createTransaction,
  updateTransaction,
  deleteTransaction,
} from '../api/budget';

interface SavingsTransaction {
  id: string;
  date: string;
  amount: number;
  category: string;
  note?: string;
  recurrence?: string;
  type: string;
}

interface SavingsCategory {
  id: string;
  name: string;
  type: string;
}

const Savings: React.FC = () => {
  const [transactions, setTransactions] = useState<SavingsTransaction[]>([]);
  const [categories, setCategories] = useState<SavingsCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form] = Form.useForm();

  const loadTransactions = async () => {
    setLoading(true);
    try {
      const resp = await getTransactions({ type: 'savings' });
      if (resp.ok && resp.data) setTransactions(resp.data as unknown as SavingsTransaction[]);
    } catch (error) {
      message.error('Failed to load savings transactions');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const resp = await getCategories();
      const names = resp.data?.savings ?? [];
      setCategories(names.map((name, i) => ({ id: String(i), name, type: 'savings' as const })));
    } catch (error) {
      console.error('Failed to load categories', error);
    }
  };

  useEffect(() => {
    loadTransactions();
    loadCategories();
  }, []);

  const handleAdd = () => {
    setEditingId(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: SavingsTransaction) => {
    setEditingId(record.id);
    form.setFieldsValue({
      date: dayjs(record.date),
      amount: record.amount,
      category: record.category,
      note: record.note,
      recurrence: record.recurrence,
    });
    setModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        date: values.date.format('YYYY-MM-DD'),
        amount: values.amount,
        type: 'savings',
        category: values.category,
        note: values.note || '',
        recurrence: values.recurrence || 'once',
      };

      if (editingId) {
        await updateTransaction(editingId, payload);
        message.success('Savings updated successfully');
      } else {
        await createTransaction(payload);
        message.success('Savings added successfully');
      }

      setModalVisible(false);
      form.resetFields();
      loadTransactions();
    } catch (error) {
      message.error(editingId ? 'Failed to update savings' : 'Failed to add savings');
      console.error(error);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteTransaction(id);
      message.success('Savings deleted successfully');
      loadTransactions();
    } catch (error) {
      message.error('Failed to delete savings');
      console.error(error);
    }
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 120,
      sorter: (a: SavingsTransaction, b: SavingsTransaction) =>
        new Date(a.date).getTime() - new Date(b.date).getTime(),
      render: (date: string) => dayjs(date).format('YYYY-MM-DD'),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 150,
      render: (category: string) => <Tag color="blue">{category}</Tag>,
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      align: 'right' as const,
      sorter: (a: SavingsTransaction, b: SavingsTransaction) => a.amount - b.amount,
      render: (amount: number) => (
        <span style={{ color: '#1890ff', fontWeight: 'bold' }}>
          ${amount.toFixed(2)}
        </span>
      ),
    },
    {
      title: 'Note',
      dataIndex: 'note',
      key: 'note',
      render: (note: string) => note || '-',
    },
    {
      title: 'Recurrence',
      dataIndex: 'recurrence',
      key: 'recurrence',
      width: 110,
      render: (recurrence: string) => (
        <Tag color={recurrence === 'once' ? 'default' : 'cyan'}>
          {recurrence || 'once'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      fixed: 'right' as const,
      render: (_: unknown, record: SavingsTransaction) => (
        <Space size="small">
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Delete Savings"
            description="Are you sure you want to delete this savings record?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="savings-page">
      <Card
        title="Savings Transactions"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Add Savings
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={transactions}
          loading={loading}
          rowKey="id"
          pagination={{ pageSize: 10, showSizeChanger: true }}
          locale={{ emptyText: <Empty description="No savings records" /> }}
          scroll={{ x: 800 }}
        />
      </Card>

      <Modal
        title={editingId ? 'Edit Savings' : 'Add Savings'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        width={500}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Date"
            name="date"
            rules={[{ required: true, message: 'Please select a date' }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            label="Amount"
            name="amount"
            rules={[
              { required: true, message: 'Please enter an amount' },
              {
                pattern: /^\d+(\.\d{1,2})?$/,
                message: 'Please enter a valid amount',
              },
            ]}
          >
            <InputNumber
              min={0}
              step={0.01}
              precision={2}
              style={{ width: '100%' }}
              prefix="$"
            />
          </Form.Item>

          <Form.Item
            label="Category"
            name="category"
            rules={[{ required: true, message: 'Please select a category' }]}
          >
            <Select placeholder="Select a savings category">
              {categories.map((cat) => (
                <Select.Option key={cat.id} value={cat.name}>
                  {cat.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Note" name="note">
            <Input.TextArea rows={3} placeholder="Add a note (optional)" />
          </Form.Item>

          <Form.Item label="Recurrence" name="recurrence" initialValue="once">
            <Select>
              <Select.Option value="once">Once</Select.Option>
              <Select.Option value="daily">Daily</Select.Option>
              <Select.Option value="weekly">Weekly</Select.Option>
              <Select.Option value="monthly">Monthly</Select.Option>
              <Select.Option value="yearly">Yearly</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Savings;
